// ===========================================
// HOOK: useMarcarPresentadoForm
// ===========================================
// Hook TanStack Form para marcar vencimiento como presentado

import { useForm } from '@tanstack/react-form'
import type { FormValidateOrFn } from '@tanstack/react-form'
import { format } from 'date-fns'
import {
  marcarPresentadoSchema,
  marcarPresentadoManualSchema,
  marcarPresentadoRecategorizacionSchema,
  type MarcarPresentadoFormValues,
  type MarcarPresentadoManualFormValues,
  type MarcarPresentadoRecategorizacionFormValues,
} from './marcarPresentadoSchema'

// Del schema de recategorización solo entran al tipo los campos que NO comparte con
// los otros dos. `monto_determinado` queda afuera a propósito: ahí es `number`
// requerido, y al intersectarlo el campo perdería el `null` que necesita mientras el
// input está vacío. La obligatoriedad la sigue imponiendo zod en runtime, que es
// donde corresponde; el tipo del form describe estados intermedios de tipeo.
type CamposRecategorizacion = {
  // `| null` y no solo opcional: el default TIENE que ser un valor real. Con
  // `undefined` el campo no queda registrado en el estado del form y el Select
  // no persiste lo elegido — falla en silencio, sin error de consola ni de tipos.
  // Detectado en el probe del preview (ORR-238), no por el pipeline.
  categoria_monotributo: MarcarPresentadoRecategorizacionFormValues['categoria_monotributo'] | null
  regimen_recategorizacion: MarcarPresentadoRecategorizacionFormValues['regimen_recategorizacion']
}

export type MarcarPresentadoAllValues = MarcarPresentadoFormValues &
  MarcarPresentadoManualFormValues &
  Partial<CamposRecategorizacion>

type FormValues = MarcarPresentadoAllValues

interface UseMarcarPresentadoFormOptions {
  onSubmit?: (value: FormValues) => Promise<void>
  defaultValues?: Partial<FormValues>
  esFiscal?: boolean
  /** ORR-238: vencimiento recurrente de recategorización → tercer schema. */
  esRecategorizacion?: boolean
}

export function useMarcarPresentadoForm(options: UseMarcarPresentadoFormOptions = {}) {
  const esFiscal = options.esFiscal ?? true
  const esRecategorizacion = options.esRecategorizacion ?? false

  const form = useForm({
    defaultValues: {
      fecha_presentacion: format(new Date(), 'yyyy-MM-dd'),
      numero_presentacion: null,
      monto_determinado: null,
      saldo_tipo: 'a_pagar',
      forma_pago: null,
      observaciones: null,
      mensaje_libre: '',
      // ORR-238: sin categoría preelegida — que el contador la elija a propósito.
      // `null`, NO `undefined`: ver el comentario de CamposRecategorizacion.
      categoria_monotributo: null,
      regimen_recategorizacion: 'monotributo',
      ...options.defaultValues,
    } as FormValues,
    listeners: {
      // Si el schema rechazó el monto, TanStack guarda ese error en el campo y no deja
      // enviar hasta que el monto cambie. Con «Sin movimiento» el input del monto se
      // desmonta, así que el error quedaría trabando Confirmar sin mostrarse. Al
      // cambiar el saldo se descarta: el Confirmar siguiente vuelve a correr el schema.
      onChange: ({ fieldApi, formApi }) => {
        if (fieldApi.name !== 'saldo_tipo' || !formApi.getFieldMeta('monto_determinado')) return
        formApi.setFieldMeta('monto_determinado', (prev) => ({
          ...prev,
          errorMap: { ...prev.errorMap, onSubmit: undefined },
          errorSourceMap: { ...prev.errorSourceMap, onSubmit: undefined },
        }))
      },
    },
    validators: {
      // Los 3 schemas validan subconjuntos distintos de FormValues (la union
      // de MarcarPresentadoFormValues, MarcarPresentadoManualFormValues y
      // MarcarPresentadoRecategorizacionFormValues).
      // TanStack Form acepta Standard Schema (zod incluido), pero TS no puede
      // probar que la union de los schemas sea asignable a FormValidateOrFn.
      // Runtime: zod valida correctamente el subset segun el modo. Cast a
      // unknown primero para romper el narrowing del ternary.
      onSubmit: (esFiscal
        ? marcarPresentadoSchema
        : esRecategorizacion
          ? marcarPresentadoRecategorizacionSchema
          : marcarPresentadoManualSchema) as unknown as FormValidateOrFn<FormValues>,
    },
    onSubmit: async ({ value }) => {
      if (options.onSubmit) {
        await options.onSubmit(value)
      }
    },
  })

  return { form }
}

export type UseMarcarPresentadoFormReturn = ReturnType<typeof useMarcarPresentadoForm>
export type MarcarPresentadoForm = UseMarcarPresentadoFormReturn['form']

/**
 * Helper para extraer mensaje de error de TanStack Form
 * Compatible con Zod v4 (StandardSchemaV1Issue)
 */
export function getFieldError(errors: unknown[]): string | undefined {
  const first = errors[0]
  if (first === undefined || first === null) return undefined
  if (typeof first === 'string') return first
  if (typeof first === 'object' && 'message' in first) {
    return (first as { message: string }).message
  }
  return undefined
}
